// this file is not tested with jest
/* istanbul ignore file */
import React, { FC, useEffect, useState } from 'react';
import { Map as Maplibre, Marker, LngLatBounds, LngLat } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import useResizeObserver from '@react-hook/resize-observer';
import { useTheme } from '../../../hooks/useTheme';
import './ResizableMap.module.css';
import { DriverLogo, DropOffLogo, RetailerLogo } from './icons';
import styles from './LocalDeliveryMap.module.css';

type ResizableMapProps = {
  maptilerKey: string;
  originLng?: number | null;
  originLat?: number | null;
  destinationLng?: number | null;
  destinationLat?: number | null;
  courierLng?: number | null;
  courierLat?: number | null;
  lastUpdated?: string;
  isHPU: boolean;
};

const ResizableMap: FC<ResizableMapProps> = ({
  maptilerKey,
  originLng,
  originLat,
  destinationLng,
  destinationLat,
  courierLng,
  courierLat,
  lastUpdated,
  isHPU = false,
}: ResizableMapProps) => {
  const [mapEl, setMapEl] = useState<HTMLElement | null>(null);
  const [maplibre, setMaplibre] = useState<Maplibre | null>(null);

  useEffect(() => {
    if (!mapEl) return;
    if (maplibre) return;
    setMaplibre(
      new Maplibre({
        container: mapEl,
        interactive: false,
        style: `https://api.maptiler.com/maps/bright-v2/style.json?key=${maptilerKey}`,
        trackResize: false, // trackResize only resizes on window resize. This case is covered by useResizeObserver
      }),
    );
  }, [mapEl, maplibre, maptilerKey]);

  useResizeObserver(mapEl, () => {
    maplibre?.resize();
  });

  const [originEl, setOriginEl] = useState<HTMLElement | null>(null);
  const [originMarker, setOriginMarker] = useState<Marker | null>(null);
  useEffect(() => {
    if (!maplibre) return;
    if (!originEl) return;
    if (originMarker) {
      if (typeof originLng === 'number' && typeof originLat === 'number') {
        // update existing marker
        originMarker.setLngLat([originLng, originLat]);
      } else {
        // remove exiting marker
        originMarker.remove();
        setOriginMarker(null);
      }
    } else if (typeof originLng === 'number' && typeof originLat === 'number') {
      // create missing marker
      const marker = new Marker(originEl)
        .setLngLat([originLng, originLat])
        .addTo(maplibre);
      setOriginMarker(marker);
    }
  }, [maplibre, originMarker, originEl, originLng, originLat]);

  const [destinationEl, setDestinationEl] = useState<HTMLElement | null>(null);
  const [destinationMarker, setDestinationMarker] = useState<Marker | null>(
    null,
  );
  useEffect(() => {
    if (!maplibre) return;
    if (!destinationEl) return;
    if (destinationMarker) {
      // update existing marker
      if (
        typeof destinationLng === 'number' &&
        typeof destinationLat === 'number'
      ) {
        destinationMarker.setLngLat([destinationLng, destinationLat]);
      } else {
        // remove exiting marker
        destinationMarker.remove();
        setDestinationMarker(null);
      }
    } else if (
      typeof destinationLng === 'number' &&
      typeof destinationLat === 'number'
    ) {
      // create missing marker
      const marker = new Marker(destinationEl)
        .setLngLat([destinationLng, destinationLat])
        .addTo(maplibre);
      setDestinationMarker(marker);
    }
  }, [
    maplibre,
    destinationMarker,
    destinationEl,
    destinationLng,
    destinationLat,
  ]);

  const [courierEl, setCourierEl] = useState<HTMLElement | null>(null);
  const [courierMarker, setCourierMarker] = useState<Marker | null>(null);
  useEffect(() => {
    if (!maplibre) return;
    if (!courierEl) return;
    if (courierMarker) {
      if (typeof courierLng === 'number' && typeof courierLat === 'number') {
        // update existing marker
        courierMarker.setLngLat([courierLng, courierLat]);
      } else {
        // remove exiting marker
        courierMarker.remove();
        setCourierMarker(null);
      }
    } else if (
      typeof courierLng === 'number' &&
      typeof courierLat === 'number'
    ) {
      // create missing marker
      const marker = new Marker(courierEl)
        .setLngLat([courierLng, courierLat])
        .addTo(maplibre);
      setCourierMarker(marker);
    }
  }, [maplibre, courierMarker, courierEl, courierLng, courierLat]);

  useEffect(() => {
    const bounds = new LngLatBounds();
    let count = 0;

    if (typeof originLng === 'number' && typeof originLat === 'number') {
      bounds.extend(new LngLat(originLng, originLat));
      count += 1;
    }
    if (
      typeof destinationLng === 'number' &&
      typeof destinationLat === 'number'
    ) {
      bounds.extend(new LngLat(destinationLng, destinationLat));
      count += 1;
    }
    if (typeof courierLng === 'number' && typeof courierLat === 'number') {
      bounds.extend(new LngLat(courierLng, courierLat));
      count += 1;
    }
    maplibre?.fitBounds(bounds, { padding: 50, duration: 0 });
    if (count === 1) {
      maplibre?.setZoom(14);
    }
  }, [
    maplibre,
    originLng,
    originLat,
    destinationLng,
    destinationLat,
    courierLng,
    courierLat,
  ]);

  const { bodytextDefaultNone, iconsPrimaryNone, buttonsPrimaryDefault } =
    useTheme();

  return (
    <div
      className={styles.mapContainer}
      ref={setMapEl}
      style={{ height: '100%' }}
    >
      {isHPU ? (
        <>
          <DropOffLogo ref={setOriginEl} s={iconsPrimaryNone} />
          <DriverLogo ref={setCourierEl} s={buttonsPrimaryDefault} />
        </>
      ) : (
        <>
          {originLat && originLng && (
            <RetailerLogo ref={setOriginEl} s={iconsPrimaryNone} />
          )}
          {destinationLat && destinationLng && (
            <DropOffLogo ref={setDestinationEl} s={iconsPrimaryNone} />
          )}
          {courierLat && courierLng && (
            <DriverLogo ref={setCourierEl} s={buttonsPrimaryDefault} />
          )}
        </>
      )}
      <div className={styles.timestamp} style={{ ...bodytextDefaultNone }}>
        {lastUpdated}
      </div>
    </div>
  );
};

export default ResizableMap;
